import { useCallback, useEffect, useMemo } from 'react';

import qs from 'qs';
import { useHistory } from 'react-router-dom';

export type QueryStateValue = string | qs.ParsedQs | string[] | qs.ParsedQs[] | undefined;

export const useQueryState = <T>(query: string, initialValue?: T): [QueryStateValue, (value: T) => void] => {
    const history = useHistory();

    const setQuery = useCallback(
        (value: T) => {
            const existingQueries = qs.parse(window.location.search, {
                ignoreQueryPrefix: true,
            });

            const queryString = qs.stringify(
                { ...existingQueries, [query]: value },
                { skipNulls: true, encode: false },
            );
            history.replace({
                search: queryString,
            });
        },
        [history, query],
    );

    const value = useMemo(() => {
        const queryInUrl = qs.parse(window.location.search, { ignoreQueryPrefix: true })[query];
        return queryInUrl || initialValue;
    }, [window.location.search, initialValue]);

    /**
     * Checks if the query parameter is already in the URL
     * If the parameter is missing but initialValue is provided it sets that query parameter in the URL using the setQuery function.
     */
    useEffect(() => {
        const queryInUrl = qs.parse(window.location.search, { ignoreQueryPrefix: true })[query];
        if (!queryInUrl && initialValue) {
            setQuery(initialValue);
        }
    }, []);

    return [value, setQuery];
};
